import React, { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  X,
  UserPlus,
  Users,
  PlusCircle,
  SlidersHorizontal,
  Check,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Card } from '@/components/ui/card'
import { PageHeader, StatusBadge } from '@/components/shared'
import { employeeService } from '@/services/employeeService'
import { useAuthStore } from '@/store/authStore'
import EmployeeForm from '@/components/Employee/EmployeeForm'

const Employees = () => {
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState(null)
  const [deleteEmployee, setDeleteEmployee] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Check if user can add employees
  const canAddEmployee = ['admin', 'hr'].includes(user?.role)
  
  const canEditEmployee = (employee) => {
    if (user?.role === 'hr') {
      return employee.user_id !== user.id && employee.user?.role !== 'admin'
    }
    if (user?.role === 'admin') {
      return true
    }
    return false
  }
  
  const canDeleteEmployee = (employee) => {
    if (user?.role === 'hr') {
      return employee.user_id !== user.id && employee.user?.role !== 'admin'
    }
    if (user?.role === 'admin') {
      return true
    }
    return false
  }

  // Fetch employees
  const { data: employeesData, isLoading } = useQuery(
    'employees',
    employeeService.getAllEmployees,
    {
      onError: () => {
        toast.error('Failed to fetch employees')
      },
    }
  )

  const employees = employeesData?.data?.employees || []

  // Filter employees
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const matchesSearch = 
        employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesDepartment = !departmentFilter || employee.department === departmentFilter
      const matchesStatus = !statusFilter || employee.user?.status === statusFilter

      return matchesSearch && matchesDepartment && matchesStatus
    })
  }, [employees, searchTerm, departmentFilter, statusFilter])

  // Get unique departments and statuses for filters
  const departments = useMemo(() => {
    const employeeDepartments = [...new Set(employees.map(emp => emp.department).filter(Boolean))]
    const commonDepartments = ['Engineering', 'HR', 'Marketing', 'Sales', 'Finance', 'Operations', 'Design', 'Support']
    return [...new Set([...employeeDepartments, ...commonDepartments])].sort()
  }, [employees])

  const statuses = ['active', 'inactive', 'pending_invitation']

  const handleAddSuccess = () => {
    setIsAddModalVisible(false)
    queryClient.invalidateQueries('employees')
  }

  const handleResendInvitation = async (employeeId, employeeName) => {
    try {
      const response = await employeeService.resendInvitation(employeeId)
      if (response.success) {
        toast.success(`Invitation email resent to ${employeeName}`)
        queryClient.invalidateQueries('employees')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend invitation')
    }
  }

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee)
    setIsEditModalVisible(true)
  }

  const handleEditSuccess = () => {
    setIsEditModalVisible(false)
    setEditingEmployee(null)
    queryClient.invalidateQueries('employees')
  }

  const handleStatusChange = async (employeeId, newStatus) => {
    try {
      const response = await employeeService.updateEmployeeStatus(employeeId, newStatus)
      if (response.success) {
        toast.success(`Employee status updated to ${newStatus}`)
        queryClient.invalidateQueries('employees')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update employee status')
    }
  }

  const handleDeleteEmployee = async () => {
    if (!deleteEmployee) return
    try {
      const response = await employeeService.deleteEmployee(deleteEmployee.id)
      if (response.success) {
        toast.success(`${deleteEmployee.first_name} ${deleteEmployee.last_name} has been deleted`)
        queryClient.invalidateQueries('employees')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete employee')
    } finally {
      setDeleteEmployee(null)
    }
  }

  const getStatusKey = (status) => {
    const statusMap = {
      active: 'active',
      pending_invitation: 'invited',
      inactive: 'inactive',
    }
    return statusMap[status] || status
  }

  const clearFilters = () => {
    setSearchTerm('')
    setDepartmentFilter('')
    setStatusFilter('')
  }

  const hasFilters = searchTerm || departmentFilter || statusFilter

  return (
    <div>
      <PageHeader
        title="Employees"
        description="Manage your team members and their information"
        toolbar={
          <>
            {/* Search Input */}
            <div className="relative w-full md:w-auto md:min-w-[150px] lg:min-w-[180px]">
              <Input
                placeholder="Filter employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 text-sm px-3 py-1"
              />
            </div>

            {/* Department Filter Button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-3 border-dashed text-sm font-normal">
                  <PlusCircle className="mr-2 h-3.5 w-3.5" />
                  Department
                  {departmentFilter && (
                    <>
                      <Separator orientation="vertical" className="mx-2 h-3.5" />
                      <Badge variant="secondary" className="rounded-sm px-1 text-xs font-normal">
                        {departmentFilter}
                      </Badge>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <div className="p-2">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1.5">Department</div>
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                    onClick={() => setDepartmentFilter('')}
                  >
                    <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${!departmentFilter ? 'bg-primary border-primary' : 'border-input'}`}>
                      {!departmentFilter && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    All Departments
                  </div>
                  {departments.map(dept => (
                    <div
                      key={dept}
                      className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                      onClick={() => setDepartmentFilter(dept === departmentFilter ? '' : dept)}
                    >
                      <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${departmentFilter === dept ? 'bg-primary border-primary' : 'border-input'}`}>
                        {departmentFilter === dept && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      {dept}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Status Filter Button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-3 border-dashed text-sm font-normal">
                  <PlusCircle className="mr-2 h-3.5 w-3.5" />
                  Status
                  {statusFilter && (
                    <>
                      <Separator orientation="vertical" className="mx-2 h-3.5" />
                      <Badge variant="secondary" className="rounded-sm px-1 text-xs font-normal">
                        {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1).replace('_', ' ')}
                      </Badge>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <div className="p-2">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1.5">Status</div>
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                    onClick={() => setStatusFilter('')}
                  >
                    <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${!statusFilter ? 'bg-primary border-primary' : 'border-input'}`}>
                      {!statusFilter && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    All Statuses
                  </div>
                  {statuses.map(status => (
                    <div
                      key={status}
                      className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                      onClick={() => setStatusFilter(status === statusFilter ? '' : status)}
                    >
                      <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${statusFilter === status ? 'bg-primary border-primary' : 'border-input'}`}>
                        {statusFilter === status && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-3 text-sm font-normal">
                <X className="mr-2 h-3.5 w-3.5" />
                Reset
              </Button>
            )}

            {/* Right side actions */}
            <div className="flex items-center gap-2 md:ml-auto">
              <Button variant="outline" size="sm" className="h-8 px-3 text-sm font-normal">
                <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
                View
              </Button>
              {canAddEmployee && (
                <Button size="sm" className="h-8 px-3 text-sm" onClick={() => setIsAddModalVisible(true)}>
                  <Plus className="mr-2 h-3.5 w-3.5" />
                  Add Employee
                </Button>
              )}
            </div>
          </>
        }
      />

      {/* Results Summary */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{filteredEmployees.length}</span> of <span className="font-medium text-foreground">{employees.length}</span> employees
        </p>
      </div>

      {/* Table */}
      <Card>
        <div className="rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Employee ID</TableHead>
                <TableHead className="bg-muted/50 font-medium">Name</TableHead>
                <TableHead className="bg-muted/50 font-medium">Email</TableHead>
                <TableHead className="bg-muted/50 font-medium">Department</TableHead>
                <TableHead className="bg-muted/50 font-medium">Job Title</TableHead>
                <TableHead className="bg-muted/50 font-medium">Status</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="h-8 w-8 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No employees found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow key={employee.id} className="hover:bg-muted/50 border-border">
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto text-primary font-medium"
                        onClick={() => navigate(`/employees/${employee.id}`)}
                      >
                        {employee.employee_id}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{`${employee.first_name} ${employee.last_name}`}</TableCell>
                    <TableCell className="text-muted-foreground">{employee.user?.email || 'N/A'}</TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>{employee.job_title}</TableCell>
                    <TableCell>
                      <StatusBadge status={getStatusKey(employee.user?.status)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {canAddEmployee && employee.user?.status === 'pending_invitation' && (
                            !(user?.role === 'hr' && employee.user?.role === 'admin') && (
                              <DropdownMenuItem onClick={() => handleResendInvitation(employee.id, `${employee.first_name} ${employee.last_name}`)}>
                                <Mail className="mr-2 h-4 w-4" />
                                Resend Invitation
                              </DropdownMenuItem>
                            )
                          )}
                          {canEditEmployee(employee) && (
                            <DropdownMenuItem onClick={() => handleEditEmployee(employee)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit Employee
                            </DropdownMenuItem>
                          )}
                          {canAddEmployee && employee.user_id !== user?.id && (
                            !(user?.role === 'hr' && employee.user?.role === 'admin') && (
                              <>
                                <DropdownMenuSeparator />
                                {employee.user?.status === 'active' ? (
                                  <DropdownMenuItem onClick={() => handleStatusChange(employee.id, 'inactive')}>
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : employee.user?.status === 'inactive' && (
                                  <DropdownMenuItem onClick={() => handleStatusChange(employee.id, 'active')}>
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                              </>
                            )
                          )}
                          {canDeleteEmployee(employee) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteEmployee(employee)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add Employee Modal */}
      <Dialog open={isAddModalVisible} onOpenChange={setIsAddModalVisible}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <UserPlus className="h-4 w-4 text-primary" />
              </div>
              Add New Employee
            </DialogTitle>
            <DialogDescription>
              Fill in the details to create a new employee account.
            </DialogDescription>
          </DialogHeader>
          <EmployeeForm 
            onSuccess={handleAddSuccess}
            onCancel={() => setIsAddModalVisible(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={isEditModalVisible} onOpenChange={setIsEditModalVisible}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-100">
                <Pencil className="h-4 w-4 text-emerald-600" />
              </div>
              Edit Employee
            </DialogTitle>
            <DialogDescription>
              Update the employee's information.
            </DialogDescription>
          </DialogHeader>
          {editingEmployee && (
            <EmployeeForm 
              employee={editingEmployee}
              onSuccess={handleEditSuccess}
              onCancel={() => setIsEditModalVisible(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteEmployee} onOpenChange={() => setDeleteEmployee(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteEmployee?.first_name} {deleteEmployee?.last_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmployee} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Employees
